import React, { useEffect, useMemo, useRef, useState } from 'react'
import { alpha, Box, IconButton, Typography, useTheme } from '@mui/material'
import { useCartStore } from '@/store/cart/cart-store'
import { selectCartItems } from '@/store/cart/cart.selectors'
import { formatMoney, getCurrencyCode } from '@/utils/locale-format'
import { getItemTotal } from '@/utils/cart-utils'
import { selectStableNextSavings, useCartPricingSnapshot } from '@/selectors/cartSelectors'
import { PriceSummary, RewardSummary } from '@/components/pricing'
import AppButton from '@/components/ui/AppButton'
import { buildCartDrawerLineKey } from '@/utils/react-key.util'
import { useOptionalBillingPreviewSync } from '@/context/BillingPreviewSyncProvider'

/** Single stacking layer for all bottom sheets / modal overlays (above BottomStack 1300, MUI modal 1300). */
export const SHEET_Z_INDEX = 1400

type SheetOverlayProps = {
  open: boolean
  onBackdropClick: () => void
  children: React.ReactNode
}

let bodyScrollLockCount = 0
let bodyScrollLockSavedY = 0
let bodyScrollLockPrevOverflow = ''
let bodyScrollLockPrevHtmlOverflow = ''

function acquireSheetBodyScrollLock(): void {
  if (bodyScrollLockCount === 0) {
    bodyScrollLockSavedY = window.scrollY
    bodyScrollLockPrevOverflow = document.body.style.overflow
    bodyScrollLockPrevHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
  }
  bodyScrollLockCount += 1
}

function releaseSheetBodyScrollLock(): void {
  bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1)
  if (bodyScrollLockCount !== 0) return
  document.body.style.overflow = bodyScrollLockPrevOverflow
  document.documentElement.style.overflow = bodyScrollLockPrevHtmlOverflow
  window.scrollTo(0, bodyScrollLockSavedY)
}

export function SheetOverlay({ open, onBackdropClick, children }: SheetOverlayProps): React.ReactElement {
  useEffect(() => {
    if (!open) return
    acquireSheetBodyScrollLock()
    return () => {
      releaseSheetBodyScrollLock()
    }
  }, [open])

  return (
    <Box
      aria-hidden={!open}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: SHEET_Z_INDEX,
        pointerEvents: open ? 'auto' : 'none',
        overscrollBehavior: 'none',
      }}
    >
      {/* Backdrop: receives taps; sits behind sheet */}
      <Box
        component="button"
        type="button"
        aria-label="Close"
        onClick={(e) => {
          e.stopPropagation()
          onBackdropClick()
        }}
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          margin: 0,
          padding: 0,
          border: 'none',
          cursor: 'pointer',
          display: 'block',
          backgroundColor: alpha('#000', 0.4),
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          opacity: open ? 1 : 0,
          transition: 'opacity 220ms ease',
          pointerEvents: open ? 'auto' : 'none',
          WebkitTapHighlightColor: 'transparent',
        }}
      />
      {/* Sheet region: safe-area inset + non-interactive gutter; children capture pointers */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'none',
          boxSizing: 'border-box',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            pointerEvents: 'auto',
            width: '100%',
            minHeight: 0,
            flex: '0 1 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}

type CartDrawerProps = {
  open: boolean
  onClose: () => void
  onCheckout?: () => void
}

function CartDrawer({ open, onClose, onCheckout }: CartDrawerProps): React.ReactElement {
  const theme = useTheme()
  const cartItems = useCartStore(selectCartItems)
  const addItem = useCartStore((s) => s.addItem)
  const removeOneUnit = useCartStore((s) => s.removeOneUnit)
  const cartPricing = useCartPricingSnapshot()
  const pricingSync = useOptionalBillingPreviewSync()
  const { subtotal: clientSubtotal, discount: clientDiscount, total: clientTotal } = cartPricing
  const authoritative = pricingSync?.pricingCommitted
  const cartSubtotal = authoritative?.subtotal ?? clientSubtotal
  const cartLedgerDiscount = authoritative?.totalDiscount ?? clientDiscount
  const cartTotalSavings = authoritative != null ? authoritative.totalSavings : clientDiscount
  const cartTotal = authoritative?.total ?? clientTotal
  const stableNextSavings = useMemo(
    () => selectStableNextSavings(authoritative?.nextSavings ?? cartPricing.nextSavings),
    [authoritative?.nextSavings, cartPricing.nextSavings]
  )
  const [dragY, setDragY] = useState(0)
  const touchStartYRef = useRef<number | null>(null)
  const isDraggingRef = useRef(false)

  const totalQuantity = useMemo(
    () => cartItems.reduce((sum, item) => sum + Math.max(0, Number(item.quantity) || 0), 0),
    [cartItems]
  )

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  useEffect(() => {
    if (!open) setDragY(0)
  }, [open])

  const onTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    touchStartYRef.current = event.touches[0]?.clientY ?? null
    isDraggingRef.current = true
  }

  const onTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current || touchStartYRef.current == null) return
    const currentY = event.touches[0]?.clientY ?? touchStartYRef.current
    const delta = Math.max(0, currentY - touchStartYRef.current)
    setDragY(delta)
  }

  const onTouchEnd = () => {
    isDraggingRef.current = false
    touchStartYRef.current = null
    if (dragY > 80) {
      setDragY(0)
      onClose()
      return
    }
    setDragY(0)
  }

  const handleIncrement = (index: number) => {
    const line = cartItems[index]
    if (!line) return
    addItem({
      menu_item_id: String(line.menu_item_id),
      item_name: line.item_name,
      item_price: line.item_price,
      category: line.category,
      restaurant_id: String(line.restaurant_id),
      selected_modifiers: line.selected_modifiers,
      selectedCombos: line.selectedCombos,
      comboTotalPerUnit: line.comboTotalPerUnit,
    })
  }

  const handleDecrement = (index: number) => {
    const line = cartItems[index]
    if (!line) return
    removeOneUnit({
      menu_item_id: String(line.menu_item_id),
      selected_modifiers: line.selected_modifiers,
      selectedCombos: line.selectedCombos,
    })
  }

  return (
    <SheetOverlay open={open} onBackdropClick={onClose}>
      <Box
        role="dialog"
        aria-modal="true"
        aria-label="Cart drawer"
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 8,
          mx: 'auto',
          px: { xs: 1.5, sm: 2 },
          width: '100%',
          maxWidth: 560,
          maxHeight: '82dvh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.common.white,
          borderRadius: 4,
          boxShadow: '0 18px 48px rgba(2, 6, 23, 0.34)',
          transform: open ? `translateY(${dragY}px)` : 'translateY(100%)',
          transition: dragY > 0 ? 'none' : 'transform 260ms cubic-bezier(0.22, 1, 0.36, 1)',
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.common.black, 0.06)}`,
        }}
      >
        <Box
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          sx={{
            px: 2,
            pt: 1,
            pb: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 5,
              borderRadius: 999,
              mx: 'auto',
              mb: 1,
              backgroundColor: alpha(theme.palette.text.primary, 0.24),
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Your Cart ({totalQuantity})
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
          {cartItems.length === 0 ? (
            <Typography color="text.secondary">Your cart is empty.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {cartItems.map((item, index) => (
                <Box
                  key={buildCartDrawerLineKey(index, item.item_name, item.quantity, getItemTotal(item))}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: 1.5,
                    backgroundColor: alpha(theme.palette.text.primary, 0.02),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600 }} noWrap>
                        {item.item_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatMoney(getItemTotal(item))}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton size="small" onClick={() => handleDecrement(index)} aria-label="Decrease quantity">
                        -
                      </IconButton>
                      <Typography sx={{ minWidth: 16, textAlign: 'center' }}>{item.quantity}</Typography>
                      <IconButton size="small" onClick={() => handleIncrement(index)} aria-label="Increase quantity">
                        +
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            p: 2,
            pb: 'max(env(safe-area-inset-bottom, 0px), 12px)',
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {cartItems.length > 0 ? (
            <Box sx={{ mb: 1.5 }}>
              <RewardSummary savings={cartTotalSavings} nextSavings={stableNextSavings} currency={getCurrencyCode()} />
              <PriceSummary
                subtotal={cartSubtotal}
                discount={cartLedgerDiscount}
                total={cartTotal}
                subtotalLabel="Subtotal"
                totalLabel="Total"
                accessibilityTotalName="Cart total"
              />
            </Box>
          ) : null}
          <AppButton
            fullWidth
            variantType="primary"
            onClick={onCheckout}
            disabled={cartItems.length === 0}
            sx={{
              height: 46,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'none',
            }}
          >
            Checkout
          </AppButton>
        </Box>
      </Box>
    </SheetOverlay>
  )
}

export default React.memo(CartDrawer)
